import React, { useState, useRef } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUpload, FiDownload, FiFileText, FiCheck, FiX } = FiIcons;

const ImportExport = ({ checklists, setChecklists, darkMode }) => {
  const [importStatus, setImportStatus] = useState(null);
  const fileInputRef = useRef();

  const exportChecklists = () => {
    const dataStr = JSON.stringify(checklists, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `n1p-checklists-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedData = JSON.parse(e.target.result);
        
        // Validate data structure
        if (!Array.isArray(importedData)) {
          throw new Error('Formato inválido: dados devem ser um array');
        }

        const validChecklists = importedData.filter(checklist => {
          return checklist && 
                 typeof checklist.id !== 'undefined' &&
                 typeof checklist.title === 'string' &&
                 Array.isArray(checklist.items);
        });

        if (validChecklists.length === 0) {
          throw new Error('Nenhuma lista válida encontrada');
        }

        // Merge with existing checklists, avoiding duplicates
        const existingIds = new Set(checklists.map(cl => cl.id));
        const newChecklists = validChecklists.filter(cl => !existingIds.has(cl.id));
        
        setChecklists([...checklists, ...newChecklists]);
        setImportStatus({
          type: 'success',
          message: `${newChecklists.length} lista(s) importada(s) com sucesso!`
        });

      } catch (error) {
        setImportStatus({
          type: 'error',
          message: `Erro ao importar: ${error.message}`
        });
      }

      // Clear file input
      event.target.value = '';
    };

    reader.readAsText(file);
  };

  const clearImportStatus = () => {
    setImportStatus(null);
  };

  const exportSingleChecklist = (checklist) => {
    const dataStr = JSON.stringify([checklist], null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `${checklist.title.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Import & Export</h1>
        <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Gerencie suas listas de marcos importando e exportando dados
        </p>
      </div>

      {importStatus && (
        <div className={`mb-6 p-4 rounded-lg flex items-center justify-between ${
          importStatus.type === 'success' 
            ? 'bg-green-100 border border-green-300 text-green-800' 
            : 'bg-red-100 border border-red-300 text-red-800'
        }`}>
          <div className="flex items-center space-x-2">
            <SafeIcon icon={importStatus.type === 'success' ? FiCheck : FiX} />
            <span>{importStatus.message}</span>
          </div>
          <button
            onClick={clearImportStatus}
            className="text-current hover:opacity-70"
          >
            <SafeIcon icon={FiX} />
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Import Section */}
        <div className={`rounded-xl p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <div className="flex items-center space-x-3 mb-4">
            <SafeIcon icon={FiUpload} className="text-2xl text-blue-600" />
            <h2 className="text-xl font-bold">Importar Listas</h2>
          </div>
          
          <p className={`mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Importe listas de marcos de um arquivo JSON exportado anteriormente.
          </p>

          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleImport}
            className="hidden"
          />

          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
          >
            <SafeIcon icon={FiUpload} />
            <span>Selecionar Arquivo JSON</span>
          </button>

          <div className={`mt-4 p-3 rounded border ${
            darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'
          }`}>
            <p className="text-sm font-medium mb-2">Formato esperado:</p>
            <pre className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
{`[
  {
    "id": 123456789,
    "title": "Título da Lista",
    "description": "Descrição...",
    "items": [...]
  }
]`}
            </pre>
          </div>
        </div>

        {/* Export Section */}
        <div className={`rounded-xl p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <div className="flex items-center space-x-3 mb-4">
            <SafeIcon icon={FiDownload} className="text-2xl text-green-600" />
            <h2 className="text-xl font-bold">Exportar Listas</h2>
          </div>
          
          <p className={`mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Exporte todas suas listas para um arquivo JSON para backup ou compartilhamento.
          </p>

          <button
            onClick={exportChecklists}
            disabled={checklists.length === 0}
            className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
          >
            <SafeIcon icon={FiDownload} />
            <span>Exportar Todas ({checklists.length})</span>
          </button>

          {checklists.length === 0 && (
            <p className={`mt-2 text-sm text-center ${
              darkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              Nenhuma lista para exportar
            </p>
          )}
        </div>
      </div>

      {/* Individual Checklists */}
      {checklists.length > 0 && (
        <div className={`rounded-xl p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <div className="flex items-center space-x-3 mb-6">
            <SafeIcon icon={FiFileText} className="text-2xl text-purple-600" />
            <h2 className="text-xl font-bold">Exportar Listas Individuais</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {checklists.map((checklist) => (
              <div
                key={checklist.id}
                className={`p-4 rounded-lg border ${
                  darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'
                }`}
              >
                <h3 className="font-semibold mb-2 truncate">{checklist.title}</h3>
                <p className={`text-sm mb-3 ${
                  darkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  {checklist.items.length} marcos
                </p>
                <button
                  onClick={() => exportSingleChecklist(checklist)}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white py-2 px-3 rounded text-sm transition-colors flex items-center justify-center space-x-2"
                >
                  <SafeIcon icon={FiDownload} />
                  <span>Exportar</span>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className={`mt-8 rounded-xl p-6 ${
        darkMode ? 'bg-blue-900/20 border border-blue-700' : 'bg-blue-50 border border-blue-200'
      }`}>
        <h3 className="font-bold mb-3">Como usar Import/Export:</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
          <div>
            <h4 className="font-semibold mb-2">Exportar:</h4>
            <ul className="space-y-1">
              <li>• Clique em "Exportar Todas" para baixar todas as listas</li>
              <li>• Ou exporte listas individuais usando os botões específicos</li>
              <li>• O arquivo será salvo em formato JSON</li>
              <li>• Use para backup ou compartilhamento</li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">Importar:</h4>
            <ul className="space-y-1">
              <li>• Clique em "Selecionar Arquivo JSON"</li>
              <li>• Escolha um arquivo JSON exportado anteriormente</li>
              <li>• As listas serão adicionadas às existentes</li>
              <li>• Listas duplicadas (mesmo ID) serão ignoradas</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportExport;